import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Order, OrderState, CreateOrderRequest, OrderTracking } from '../../types/order';

const initialState: OrderState = {
  currentOrder: null,
  orderHistory: [],
  cart: [],
  isLoading: false,
  error: null,
  trackingInfo: null,
};

// Async thunks
export const createOrder = createAsyncThunk(
  'order/createOrder',
  async (orderData: CreateOrderRequest, { rejectWithValue }) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token') || localStorage.getItem('guestToken');

      // Prepare headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };

      // Only add Authorization header if we have a token
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Add company parameter to order data
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const orderDataWithCompany = {
        ...orderData,
        company
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/order`, {
        method: 'POST',
        headers,
        body: JSON.stringify(orderDataWithCompany),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message || 'Failed to create order');
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchOrderHistory = createAsyncThunk(
  'order/fetchHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/order/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('guestToken')}`
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const trackOrder = createAsyncThunk(
  'order/trackOrder',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/order/track/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('guestToken')}`
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'order/cancelOrder',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/order/cancel/${orderId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('guestToken')}`
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      return await response.json();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateOrderStatus: (state, action: PayloadAction<{ orderId: string; status: Order['status'] }>) => {
      // Update current order
      if (state.currentOrder && state.currentOrder.id === action.payload.orderId) {
        state.currentOrder.status = action.payload.status;
      }

      // Update in history
      const historyOrder = state.orderHistory.find(order => order.id === action.payload.orderId);
      if (historyOrder) {
        historyOrder.status = action.payload.status;
      }

      // Update tracking info
      if (state.trackingInfo && state.trackingInfo.orderId === action.payload.orderId) {
        state.trackingInfo.status = action.payload.status;
      }
    },
    addOrderUpdate: (state, action: PayloadAction<{
      orderId: string;
      update: {
        timestamp: string;
        status: Order['status'];
        message: string;
        location?: string;
      };
    }>) => {
      if (state.trackingInfo && state.trackingInfo.orderId === action.payload.orderId) {
        state.trackingInfo.updates.push(action.payload.update);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Create order
      .addCase(createOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload.order;
        state.orderHistory.unshift(action.payload.order);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch order history
      .addCase(fetchOrderHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orderHistory = action.payload.orders || [];
      })
      .addCase(fetchOrderHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Track order
      .addCase(trackOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(trackOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.trackingInfo = action.payload.tracking;
      })
      .addCase(trackOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Cancel order
      .addCase(cancelOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.currentOrder && state.currentOrder.id === action.payload.orderId) {
          state.currentOrder.status = 'cancelled';
        }
        const historyOrder = state.orderHistory.find(order => order.id === action.payload.orderId);
        if (historyOrder) {
          historyOrder.status = 'cancelled';
        }
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearCurrentOrder,
  clearError,
  updateOrderStatus,
  addOrderUpdate,
} = orderSlice.actions;

export default orderSlice.reducer;